import type { AppRouter } from "@micropod/core";
import { createTRPCClient, httpBatchLink } from "@trpc/client";

/**
 * The ONLY module in the console that may reach the API.
 *
 * `bun run lint:arch` fails the build on a `fetch` to any http(s) origin anywhere else in
 * `apps/console`. The boundary erodes one convenient call at a time otherwise, and then
 * business logic starts living in two places.
 */

/** Local Workers in dev, the deployed one otherwise. No env file needed either way. */
const API_ORIGIN =
  import.meta.env.VITE_API_ORIGIN ??
  (import.meta.env.DEV ? "http://localhost:8787" : "https://api.micropod.dev");

/** Bearer, not cookies — the console and API are on different subdomains. See docs/07 §3. */
let token: string | null = localStorage.getItem("mp_token");

export function getToken(): string | null {
  return token;
}

export function setToken(next: string | null): void {
  token = next;
  if (next) localStorage.setItem("mp_token", next);
  else localStorage.removeItem("mp_token");
}

/**
 * What to do when the server says this token is no longer good.
 *
 * A bearer token outlives the tab it was minted in, so it can expire or be revoked
 * mid-session — and until this existed, every subsequent call simply failed and the
 * console kept rendering a shell around data that would never arrive. The gate needs to
 * hear about it once, from wherever it happens.
 */
let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(fn: (() => void) | null): void {
  onUnauthorized = fn;
}

export const api = createTRPCClient<AppRouter>({
  links: [
    httpBatchLink({
      url: `${API_ORIGIN}/trpc`,
      headers: () => (token ? { authorization: `Bearer ${token}` } : {}),
      // Wrapped rather than replaced: tRPC still owns the request, this only watches the
      // response for the one status the whole app has to react to.
      fetch: async (input, init) => {
        const res = await globalThis.fetch(input as RequestInfo, init as RequestInit);
        if (res.status === 401) {
          setToken(null);
          onUnauthorized?.();
        }
        return res;
      },
    }),
  ],
});

export { API_ORIGIN };
